/**
 * Hitting things that are not blocks.
 *
 * The voxel raycast marches the grid and cannot see a creature standing in it,
 * so anything alive is tested separately and whichever is nearer wins. That
 * test was written once for the villagers and needed for the orcs the moment
 * they existed, which is what it is doing here instead of there.
 */

/** Anything that stands somewhere. */
export interface Positioned {
  x: number;
  y: number;
  z: number;
}

/**
 * Distance along the ray to the box, or null if it misses.
 *
 * The classic slab method: clip the ray against each pair of parallel faces
 * and keep the overlap. A zero direction component is handled by the
 * infinities that division produces, which is why there is no special case
 * for it.
 */
export function raySlab(
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  x0: number, y0: number, z0: number,
  x1: number, y1: number, z1: number,
): number | null {
  let near = 0;
  let far = Infinity;

  const axes: readonly (readonly [number, number, number, number])[] = [
    [ox, dx, x0, x1], [oy, dy, y0, y1], [oz, dz, z0, z1],
  ];
  for (const [origin, dir, lo, hi] of axes) {
    const inv = 1 / dir;
    let t0 = (lo - origin) * inv;
    let t1 = (hi - origin) * inv;
    if (t0 > t1) [t0, t1] = [t1, t0];
    // NaN comes from a zero direction and a coordinate exactly on the face;
    // treating that as a miss is both simpler and safer than the alternative.
    if (Number.isNaN(t0) || Number.isNaN(t1)) return null;
    near = Math.max(near, t0);
    far = Math.min(far, t1);
    if (near > far) return null;
  }
  return near;
}

/**
 * The nearest thing the ray passes through, within `maxDistance`.
 *
 * The box stands on the entity's feet and is as wide and as tall as it is
 * given - a person's box, not a block's, so a ray a whole block to the side
 * misses and you cannot hit somebody through the corner of a wall.
 */
export function pickEntity<T extends Positioned>(
  list: readonly T[],
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  maxDistance: number,
  width: number,
  height: number,
): { entity: T; distance: number } | null {
  let best: { entity: T; distance: number } | null = null;
  const half = width / 2;

  for (const entity of list) {
    const distance = raySlab(
      ox, oy, oz, dx, dy, dz,
      entity.x - half, entity.y, entity.z - half,
      entity.x + half, entity.y + height, entity.z + half,
    );
    if (distance === null || distance > maxDistance) continue;
    if (best === null || distance < best.distance) best = { entity, distance };
  }
  return best;
}
